import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, EyeOff, Eye, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const STATUS_ICON = {
  present: { icon: '✓', cls: 'text-green-400' },
  absent:  { icon: '✗', cls: 'text-red-400' },
  excused: { icon: '~', cls: 'text-yellow-400' },
};

export default function StudentReportModal({
  student, token, backendUrl, sessionTypes, sessions, onClose, onStudentUpdate,
}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const res = await axios.get(
          `${backendUrl}/api/admin/agenda/attendance/student/${student.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRecords(res.data);
      } catch {}
      setLoading(false);
    };
    fetchRecords();
  }, [student.id, backendUrl, token]);

  const handleToggleActive = async () => {
    setActionLoading(true);
    try {
      await axios.patch(
        `${backendUrl}/api/admin/agenda/students/${student.id}`,
        { active: !student.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onStudentUpdate?.();
      onClose();
    } catch {}
    finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(
        `${backendUrl}/api/admin/agenda/students/${student.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onStudentUpdate?.();
      onClose();
    } catch {}
    finally { setActionLoading(false); }
  };

  const total = records.length;
  const present = records.filter(r => r.status === 'present').length;
  const pct = total > 0 ? Math.round((present / total) * 100) : null;
  const pctColor = pct === null ? '' : pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';

  const enriched = records.map(r => {
    const session = sessions.find(s => s.id === r.sessionId);
    const type = session ? sessionTypes.find(t => t.id === session.typeId) : null;
    let dateLabel = r.date;
    try { dateLabel = format(parseISO(r.date), "dd/MM/yyyy (EEE)", { locale: ptBR }); } catch {}
    return { ...r, typeName: type?.name || 'Sessão', sessionTime: session?.time || '', dateLabel };
  });

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/10 w-full max-w-md max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold uppercase tracking-widest">{student.name}</h2>
              {student.appStudentId && (
                <span className="text-[9px] bg-white/10 text-[#A3A3A3] px-1.5 py-0.5">APP</span>
              )}
              {!student.active && (
                <span className="text-[9px] bg-white/5 text-[#555] px-1.5 py-0.5 uppercase tracking-wider">Inativo</span>
              )}
            </div>
            {student.phone && <p className="text-xs text-[#555] mt-0.5">{student.phone}</p>}
          </div>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X size={16} /></button>
        </div>

        {/* Frequência */}
        {!loading && total > 0 && (
          <div className="px-6 py-4 border-b border-white/5 flex-shrink-0">
            <div className="flex items-end justify-between mb-2">
              <span className="text-xs text-[#A3A3A3] uppercase tracking-widest">Frequência geral</span>
              <span className="text-2xl font-bold">{pct}%</span>
            </div>
            <div className="w-full bg-white/5 h-1.5">
              <div className={`h-1.5 transition-all ${pctColor}`} style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-[#555] mt-1.5">
              {present} presente{present !== 1 ? 's' : ''} de {total} sessão{total !== 1 ? 'ões' : ''} registrada{total !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Lista */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <p className="text-[#555] text-sm text-center p-8">Carregando...</p>
          ) : total === 0 ? (
            <p className="text-[#555] text-sm text-center p-8">Nenhuma presença registrada ainda.</p>
          ) : (
            <div>
              {enriched.map(r => (
                <div key={r.id} className="flex items-center justify-between px-6 py-3 border-b border-white/5 hover:bg-white/[0.02]">
                  <div>
                    <p className="text-sm">{r.typeName}</p>
                    <p className="text-xs text-[#555] capitalize">{r.dateLabel}{r.sessionTime && ` • ${r.sessionTime}`}</p>
                  </div>
                  <span className={`text-sm font-bold ${STATUS_ICON[r.status]?.cls}`}>
                    {STATUS_ICON[r.status]?.icon}{' '}
                    <span className="text-xs font-normal">
                      {r.status === 'present' ? 'Presente' : r.status === 'absent' ? 'Faltou' : 'Justificado'}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex-shrink-0">
          {confirmDelete ? (
            <div>
              <p className="text-xs text-[#A3A3A3] mb-3">
                Excluir <span className="text-white font-medium">{student.name}</span>?{' '}
                <span className="text-[#555]">As presenças registradas serão mantidas.</span>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleDelete}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white text-sm font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Excluindo...' : 'Excluir'}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-4 py-2 border border-white/10 hover:border-white/30 text-sm text-[#A3A3A3] transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="flex gap-2">
                <button
                  onClick={handleToggleActive}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-2 border border-white/10 hover:border-white/30 text-xs text-[#A3A3A3] hover:text-white transition-colors disabled:opacity-50"
                >
                  {student.active ? <EyeOff size={13} /> : <Eye size={13} />}
                  {student.active ? 'Desativar' : 'Reativar'}
                </button>
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-2 border border-white/10 hover:border-red-500/50 text-xs text-[#555] hover:text-red-400 transition-colors disabled:opacity-50"
                >
                  <Trash2 size={13} /> Excluir
                </button>
              </div>
              <button onClick={onClose}
                className="px-4 py-2 border border-white/10 hover:border-white/30 text-sm text-[#A3A3A3] transition-colors"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
